/** @file partsOf: the Art parts list, one row per impostor drawable with its filled / total slot counts. */
import { deriveSlots } from '@zilo/pose3d-format/derive/deriveSlots';
import type { Puppet } from '@zilo/pose3d-format/types/puppet';
import type { ArtPart } from './artTypes';

/** Every entry of the drawable needs a variant flag (a hat-like layer the default look leaves out). */
function variantOnly(puppet: Puppet, drawable: string): boolean {
  const d = puppet.drawables.find((x) => x.id === drawable);
  return d?.kind === 'impostor' && d.facets.length > 0 && d.facets.every((e) => e.variants.length > 0);
}

/**
 * One part per drawable that has slots, in `deriveSlots` order: label `node · drawable`, filled and total slot
 * counts, `optional` when the drawable's node is `hidden` or every entry needs a variant.
 */
export function partsOf(puppet: Puppet): ArtPart[] {
  const parts = new Map<string, ArtPart>();
  for (const slot of deriveSlots(puppet)) {
    let part = parts.get(slot.drawable);
    if (part === undefined) {
      const d = puppet.drawables.find((x) => x.id === slot.drawable);
      const node = d?.node ?? '';
      const optional = puppet.nodes[node]?.hidden === true || variantOnly(puppet, slot.drawable);
      part = { drawable: slot.drawable, node, label: `${node} · ${slot.drawable}`, filled: 0, total: 0, optional };
      parts.set(slot.drawable, part);
    }
    part.total += 1;
    if (slot.status === 'filled') part.filled += 1;
  }
  return [...parts.values()];
}
